import React, { useState, useEffect, useMemo } from 'react'
import ReportGenerationChart from '../../components/charts/ReportGenerationChart'

interface Report {
  id: string
  name: string
  type: 'daily' | 'weekly' | 'monthly' | 'custom'
  status: 'generated' | 'generating' | 'scheduled' | 'failed'
  created_at: string
  file_size?: string
  download_url?: string
}

const AnalyticsReports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([])
  const [selectedType, setSelectedType] = useState('all')
  const [isGenerating, setIsGenerating] = useState(false)

  useEffect(() => {
    const mockReports: Report[] = [
      {
        id: '1',
        name: 'Daily Performance Report',
        type: 'daily',
        status: 'generated',
        created_at: new Date().toISOString(),
        file_size: '2.4 MB',
        download_url: '/reports/daily-2025-01-08.pdf'
      },
      {
        id: '2',
        name: 'Weekly Analytics Summary',
        type: 'weekly',
        status: 'generated',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        file_size: '8.7 MB',
        download_url: '/reports/weekly-2025-w02.pdf'
      },
      {
        id: '3',
        name: 'Monthly Plant Overview',
        type: 'monthly',
        status: 'generating',
        created_at: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: '4',
        name: 'Custom Block Analysis',
        type: 'custom',
        status: 'scheduled',
        created_at: new Date(Date.now() + 3600000).toISOString()
      }
    ]
    setReports(mockReports)
  }, [])

  const generateReport = async (type: string) => {
    setIsGenerating(true)
    // Simulate report generation
    setTimeout(() => {
      const newReport: Report = {
        id: Date.now().toString(),
        name: `${type.charAt(0).toUpperCase() + type.slice(1)} Report`,
        type: type as any,
        status: 'generated',
        created_at: new Date().toISOString(),
        file_size: '3.2 MB',
        download_url: `/reports/${type}-${Date.now()}.pdf`
      }
      setReports(prev => [newReport, ...prev])
      setIsGenerating(false)
    }, 3000)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'generated': return 'text-green-600 bg-green-100'
      case 'generating': return 'text-blue-600 bg-blue-100'
      case 'scheduled': return 'text-yellow-600 bg-yellow-100'
      case 'failed': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const filteredReports = selectedType === 'all' 
    ? reports 
    : reports.filter(report => report.type === selectedType)

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics Reports</h1>
        <div className="flex gap-4">
          <select 
            value={selectedType} 
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="all">All Reports</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <button
          onClick={() => generateReport('daily')}
          disabled={isGenerating}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow hover:shadow-lg transition-shadow disabled:opacity-50"
        >
          <div className="text-center">
            <div className="text-3xl mb-2">📊</div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Daily Report</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Generate daily analytics</p>
          </div>
        </button>

        <button
          onClick={() => generateReport('weekly')}
          disabled={isGenerating}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow hover:shadow-lg transition-shadow disabled:opacity-50"
        >
          <div className="text-center">
            <div className="text-3xl mb-2">📈</div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Weekly Report</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Generate weekly summary</p>
          </div>
        </button>

        <button
          onClick={() => generateReport('monthly')}
          disabled={isGenerating}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow hover:shadow-lg transition-shadow disabled:opacity-50"
        >
          <div className="text-center">
            <div className="text-3xl mb-2">📋</div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Monthly Report</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Generate monthly overview</p>
          </div>
        </button>

        <button
          onClick={() => generateReport('custom')}
          disabled={isGenerating}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow hover:shadow-lg transition-shadow disabled:opacity-50"
        >
          <div className="text-center">
            <div className="text-3xl mb-2">⚙️</div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Custom Report</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Create custom analysis</p>
          </div>
        </button>
      </div>

      {isGenerating && (
        <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-blue-800 dark:text-blue-200">Generating report...</span>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Generated Reports ({filteredReports.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Report Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredReports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {report.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {report.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(report.status)}`}>
                      {report.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {new Date(report.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {report.file_size || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {report.status === 'generated' && report.download_url ? (
                      <div className="flex gap-2">
                        <button className="text-blue-600 hover:text-blue-900">Download</button>
                        <button className="text-gray-600 hover:text-gray-900">Share</button>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Report Generation Statistics 📊
        </h3>
        <ReportGenerationChart />
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Report Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Performance Summary</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Comprehensive plant performance metrics and KPIs
            </p>
            <button className="text-blue-600 hover:text-blue-900 text-sm">Use Template</button>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Maintenance Report</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Equipment health and maintenance recommendations
            </p>
            <button className="text-blue-600 hover:text-blue-900 text-sm">Use Template</button>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Financial Analysis</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Revenue analysis and ROI calculations
            </p>
            <button className="text-blue-600 hover:text-blue-900 text-sm">Use Template</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsReports